import React, { Fragment, memo, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import type { Edge, Node } from "reactflow"
import { ReactFlowProvider, getOutgoers, useEdgesState, useReactFlow, useStoreApi } from "reactflow"
import "reactflow/dist/style.css"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Box, CircularProgress, Grid } from "@mui/material"
import { Button, NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import queryString from "query-string"
import { v4 as uuidv4 } from "uuid"

import { InfoContainer } from "../../components/core/InfoContainer"
import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { DebouncedInput } from "../../components/form/DebouncedInput"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { CreateWorkflowRequest, UpdateWorkflowRequest } from "../../types/custom/projects"
import type { CalculatorEquation } from "../../types/generated/api/CalculatorEquation"
import type { PaginatedWorkflowListList } from "../../types/generated/api/PaginatedWorkflowListList"
import { WorkflowConstantFeature } from "../../types/generated/api/WorkflowConstantFeature"
import type { WorkflowGroupRetrieve } from "../../types/generated/api/WorkflowGroupRetrieve"
import { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import type { WorkflowList } from "../../types/generated/api/WorkflowList"
import type { WorkflowVersionRetrieve } from "../../types/generated/api/WorkflowVersionRetrieve"
import { getEnableClose, setEnableClose } from "../../utils/SessionStorageHelpers"
import { extractPageFromBackEndPaginationLink } from "../../utils/genericHelpers"
import { WorkflowCanvas } from "./components/canvas/WorkflowCanvas"
import { WorkflowControls } from "./components/canvas/WorkflowControls"
import { WorkflowSidebar } from "./components/sidebar/WorkflowSidebar"
import { WorkflowCanvasContext } from "./context/Context"
import { edgeStyles, markerEnd } from "./fixtures/workflow-fixtures"
import { useWorkflowForm } from "./hooks/forms/useWorkflowForm"
import type { ConfirmCloseDialogState, UpdateGraphParams } from "./types/interfaces"
import {
  convertRetrievedNodesToFlow,
  getEdgeLabel,
  getLayoutedElements,
  hasSchemaChanged,
} from "./utils/workflowHelpers"

import styles from "./Workflows.module.scss"
import "./components/nodes/Nodes.module.scss"

/**
 * Workflows component that manages workflow creation, editing, and visualization.
 * Provides a canvas-based interface for building and managing data processing workflows
 * with support for various node types including filters, calculators, and scripts.
 */
const Workflows = (): React.ReactElement => {
  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowsPage",
  })

  // React Flow hooks
  const { getEdges, getNode } = useReactFlow()
  const store = useStoreApi()
  const { setNodes, getNodes } = store.getState()
  const nodes = getNodes()

  // Constants
  const queryParams = useMemo(() => Object(queryString.parse(location.search)), [location.search])

  // State hooks
  // UI state
  const [canvasWorkflowOpen, setCanvasWorkflowOpen] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true)

  // React Flow state
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // Workflow state
  const [currentWorkflow, setCurrentWorkflow] = useState<{ workflowName: string; workflowId: string }>({
    workflowId: queryParams?.workflowId === "new" || !queryParams?.workflow ? "" : queryParams?.workflowId,
    workflowName: queryParams?.workflowName ?? "",
  })
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDuplicated, setIsDuplicated] = useState<boolean>(false)

  // Search and URL state
  const [searchValue, setSearchValue] = useState<string>("")
  const [searchParams, setSearchParams] = useSearchParams()

  // Dialog state
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState<ConfirmCloseDialogState>({
    isOpen: false,
  })
  const [confirmDeprecateWorkflowsDialogOpen, setConfirmDeprecateWorkflowsDialogOpen] = useState<boolean>(false)

  // Error and validation state
  const [collidingFeaturesList, setCollidingFeaturesList] = useState<Array<string>>([])
  const [lastWorkflowUpdatePayload, setLastWorkflowUpdatePayload] = useState<UpdateWorkflowRequest>()

  // Memoized values
  const query = useMemo(() => {
    return {
      page: "Workflows",
      workflowId: currentWorkflow["workflowId"],
      workflowName: currentWorkflow["workflowName"],
    }
  }, [currentWorkflow])

  // Mutations
  const createWorkflowMutation = useMutation<AxiosResponse, AxiosError, CreateWorkflowRequest>(
    KonanAPI.createWorkflow,
    {
      mutationKey: "createWorkflow",
      onSuccess: async (response) => {
        setCurrentWorkflow({ workflowName: response?.data?.name, workflowId: response?.data?.uuid })
        const workflowName = response?.data?.name
        const workflowId = response?.data?.uuid
        setSearchParams({ workflowName, workflowId })
        setEnableClose("false")
        NotificationUtils.toast(`Workflow successfully created!`, {
          severity: "success",
        })

        await Promise.all([
          queryClient.invalidateQueries("workflows"),
          queryClient.invalidateQueries("workflow"),
          queryClient.invalidateQueries(["project", projectId]),
          queryClient.invalidateQueries("workflow-version"),
          queryClient.invalidateQueries("workflow-versions"),
        ])
      },
      onError: () => {
        if (!isDuplicated) {
          const workflowName = query.workflowName
          const workflowId = "new"
          setSearchParams({ workflowName, workflowId })
          setEnableClose(isCreateMode ? "true" : "false")
          setCurrentWorkflow({
            workflowName: currentWorkflow["workflowName"],
            workflowId: "new",
          })
          setNodes([...nodes])
        }
      },
    },
  )

  // update a workflow
  const updateWorkflowMutation = useMutation<AxiosResponse, AxiosError, UpdateWorkflowRequest>(
    KonanAPI.updateWorkflow,
    {
      onSuccess: async (response) => {
        const workflowName = response?.data?.name
        const workflowId = response?.data?.uuid
        setSearchParams({ workflowName, workflowId })
        setEnableClose("false")
        setCurrentWorkflow({ workflowName: response?.data?.name, workflowId: response?.data?.uuid })
        NotificationUtils.toast(
          formik.values.isSchemaSubmit
            ? t("notifications.updateWorkflow.success.schema")
            : t("notifications.updateWorkflow.success.workflow"),
          {
            severity: "success",
          },
        )
        formik.setFieldValue("isSchemaSubmit", false)
        setIsEditMode(false)
        setConfirmDeprecateWorkflowsDialogOpen(false)
        await Promise.all([
          queryClient.invalidateQueries("workflows"),
          queryClient.invalidateQueries("workflow"),
          queryClient.invalidateQueries(["project", projectId]),
          queryClient.invalidateQueries("workflow-version"),
          queryClient.invalidateQueries("workflow-versions"),
        ])
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: ({ response }: any) => {
        setEnableClose("true")

        if (response?.data?.details && response?.data?.details?.includes("colliding with project schema")) {
          const regex = /\[([^\]]+)\]/
          const match = regex.exec(response?.data?.details)
          if (match) {
            const values = match[1]?.split(",").map((value) => value.trim())
            setCollidingFeaturesList(values as string[])
          } else {
            setCollidingFeaturesList([])
          }
          setConfirmDeprecateWorkflowsDialogOpen(true)
        } else if (response?.data?.feature_mappings?.length > 0) {
          NotificationUtils.toast(`${response?.data?.feature_mappings[0]}`, {
            severity: "error",
          })
        } else if (response?.data?.non_field_errors?.length > 0) {
          NotificationUtils.toast(`${response?.data?.non_field_errors[0]}`, {
            severity: "error",
          })
        } else if (response?.data?.details) {
          setConfirmDeprecateWorkflowsDialogOpen(false)
          NotificationUtils.toast(`${response?.data?.details}`, {
            severity: "error",
          })
        } else if (response?.data?.nodes) {
          setConfirmDeprecateWorkflowsDialogOpen(false)
          NotificationUtils.toast(`${response?.data?.nodes}`, {
            severity: "error",
          })
        } else {
          NotificationUtils.toast(t("notifications.updateWorkflow.error.schema"), {
            severity: "error",
          })
          setConfirmDeprecateWorkflowsDialogOpen(false)
        }

        setNodes([...nodes])
      },
    },
  )

  // Data fetching queries
  const {
    isLoading: isWorkflowsLoading,
    isFetchingNextPage,
    data: workflowsData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedWorkflowListList>, AxiosError>(
    ["workflows", projectId, searchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchWorkflows({
        projectId: projectId as string,
        page_size: 20,
        page: pageParam,
        search: searchValue,
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId,
    },
  )

  const {
    isLoading: isWorkflowLoading,
    data: workflow,
    isFetching,
    isRefetching,
  } = useQuery<AxiosResponse<WorkflowGroupRetrieve>, AxiosError>(
    ["workflow", projectId, currentWorkflow["workflowId"]],
    () => KonanAPI.fetchWorkflow(projectId as string, currentWorkflow["workflowId"]),
    {
      enabled: !!currentWorkflow["workflowId"] && currentWorkflow["workflowId"] !== "new" && !isEditMode && !!projectId,
      onSuccess: (res) => {
        formik.setFieldValue("workflowName", res?.data?.name)
      },
    },
  )

  // Additional memoized values
  const activeWorkflowVersion = workflow?.data?.active_version as WorkflowVersionRetrieve

  const adjustedWorkflows = useMemo(() => {
    return workflowsData?.pages?.reduce((accumulator, page) => {
      return accumulator.concat(page.data.results as WorkflowList[])
    }, [] as WorkflowList[])
  }, [workflowsData?.pages])

  const orderedWorkflows = useMemo(() => {
    if (adjustedWorkflows && adjustedWorkflows?.length > 0 && adjustedWorkflows?.length > 0) {
      return [...(adjustedWorkflows ?? [])].sort((a, b) => (a.state === "ACTIVE" ? -1 : b.state === "ACTIVE" ? 1 : 0))
    }
    return []
  }, [adjustedWorkflows])

  // Helper functions
  const initNodesAndEdges = (): void => {
    const initNodes = [
      {
        id: "1",
        data: { nodeType: "StartNode" },
        position: { x: 100, y: 100 },
        type: "StartNode",
        draggable: true,
        height: 64,
        width: 320,
      },
      {
        id: "2",
        data: { updateGraph, parentId: "1" },
        position: { x: 100, y: 250 },
        type: "AddBlockNode",
      },
    ]
    const initEdges = [
      {
        id: "e1-2",
        source: "1",
        target: "2",
        type: "custom",
        data: { text: "", updateGraph },
        animated: true,
        markerEnd,
        style: edgeStyles,
      },
    ]
    setNodes([...initNodes])
    setEdges([...initEdges])
  }

  const nodelessWorkFlow = (isAddBlockDisabled: boolean): void => {
    const initNodes = [
      {
        id: "1",
        data: { nodeType: "StartNode" },
        position: { x: 100, y: 100 },
        type: "StartNode",
        draggable: true,
        height: 64,
        width: 320,
      },
      {
        id: "2",
        data: {
          updateGraph,
          schema: activeWorkflowVersion?.schema,
          disabled: isAddBlockDisabled,
          formik,
          parentId: "1",
        },
        position: { x: 100, y: 250 },
        type: "AddBlockNode",
      },
    ]
    const initEdges = [
      {
        id: "e1-2",
        source: "1",
        target: "2",
        type: "custom",
        data: { text: "", updateGraph },
        animated: true,
        markerEnd,
        style: edgeStyles,
      },
    ]

    setNodes([...initNodes])
    setEdges([...initEdges])
  }

  const adjustCanvasNodes = (nodes: Node[], edges: Edge[]): void => {
    setTimeout(() => {
      if (
        currentWorkflow["workflowId"] !== "new" &&
        getNodes()[1]?.type !== "AddBlockNode" &&
        !updateWorkflowMutation.isLoading
      ) {
        setNodes([...nodes])
        setEdges([...edges])
        getLayoutedElements([...nodes], [...edges])
      }
    }, 10)
  }

  const handleInitNewWorkflow = (): void => {
    const workflowName = `Untitled-${workflowsData?.pages[0]?.data?.count ? workflowsData?.pages[0]?.data?.count + 1 : 1}`
    const workflowId = "new"

    setEnableClose(orderedWorkflows?.length ? "true" : "false")

    setCurrentWorkflow({
      workflowName,
      workflowId,
    })

    setSearchParams({
      workflowName: workflowName,
      workflowId: workflowId,
    })

    formik.setFieldValue("workflowName", workflowName)
    formik.setFieldValue("features", defaultFeatures)
    formik.setFieldValue("constants", defaultConstants)
    formik.setFieldValue("computedFeatures", [])
    setIsCreateMode(true)
    setIsEditMode(false)
    initNodesAndEdges()
    setCanvasWorkflowOpen(true)
  }

  const onAddNewWorkflow = (): void => {
    if (getEnableClose()) {
      setConfirmCloseDialogOpen({ isOpen: true, action: { name: "new-workflow" } })
    } else {
      handleInitNewWorkflow()
    }
  }

  const handleDuplicateWorkflow = async (): Promise<void> => {
    const workflowName = t("workflowCopyName", { workflowName: workflow?.data.name })
    const workflowId = "new"

    setSearchParams({ workflowName, workflowId })

    setEnableClose(orderedWorkflows?.length ? "true" : "false")

    setCurrentWorkflow({
      workflowName,
      workflowId,
    })

    formik.setFieldValue("workflowName", workflowName)
    setIsDuplicated(true)
    setIsCreateMode(true)
    setIsEditMode(false)
    setCanvasWorkflowOpen(true)
  }

  const handleConfirmAction = (): void => {
    switch (confirmCloseDialogOpen?.action?.name) {
      case "switch":
        setCurrentWorkflow({
          workflowName: confirmCloseDialogOpen?.action?.workflowName as string,
          workflowId: confirmCloseDialogOpen?.action?.workflowId as string,
        })
        formik.setFieldValue("workflowName", "")
        setSearchParams({
          workflowName: confirmCloseDialogOpen?.action?.workflowName as string,
          workflowId: confirmCloseDialogOpen?.action?.workflowId as string,
        })

        setEnableClose("false")
        break

      case "close":
        setCanvasWorkflowOpen(false)
        setCurrentWorkflow({
          workflowName: orderedWorkflows[0]?.name ?? "",
          workflowId: orderedWorkflows[0]?.uuid ?? "",
        })
        formik.setFieldValue("workflowName", "")
        setSearchParams({
          workflowName: orderedWorkflows[0]?.name as string,
          workflowId: orderedWorkflows[0]?.uuid as string,
        })

        setEnableClose("false")
        break

      case "new-workflow":
        handleInitNewWorkflow()
        break

      default:
        break
    }

    setConfirmCloseDialogOpen({ isOpen: false })
    setIsEditMode(false)
    setSearchParams({
      workflowName: searchParams.get("workflowName") as string,
      workflowId: searchParams.get("workflowId") as string,
    })
    setEnableClose("false")

    if (currentWorkflow["workflowId"] !== "new" && confirmCloseDialogOpen?.action?.name !== "new-workflow") {
      formik.setFieldValue("workflowName", currentWorkflow["workflowName"])
    }
  }

  const handleEditClicked = (): void => {
    setIsEditMode(true)

    setSearchParams({
      workflowName: searchParams.get("workflowName") as string,
      workflowId: searchParams.get("workflowId") as string,
    })

    setEnableClose("true")

    if (
      activeWorkflowVersion?.nodes?.length === 0 &&
      activeWorkflowVersion?.start_node === null &&
      !updateWorkflowMutation.isLoading
    ) {
      nodelessWorkFlow(false)
    } else if (
      activeWorkflowVersion?.nodes &&
      activeWorkflowVersion?.nodes?.length > 0 &&
      activeWorkflowVersion?.start_node !== null &&
      !isCreateMode &&
      !isEditMode
    ) {
      const flowData = convertRetrievedNodesToFlow(
        activeWorkflowVersion?.nodes,
        activeWorkflowVersion?.start_node ?? "0",
        "edit",
        updateGraph,
        activeWorkflowVersion?.schema,
        formik,
      )
      setTimeout(() => {
        adjustCanvasNodes([...flowData["nodes"]], [...flowData["edges"]])
      }, 100)
    }
  }

  // Default values
  const defaultFeatures = [
    {
      name: "",
      type: WorkflowInputFeature.type.TEXT,
      is_required: true,
      remove: false,
      file: false,
      id: uuidv4(),
      new: true,
      source: "-",
    },
  ]

  const defaultConstants = [
    {
      id: uuidv4(),
      name: "",
      type: WorkflowConstantFeature.type.TEXT,
      is_used: false,
      value: "",
      new: true,
    },
  ]

  // Formik initialization
  const formik = useWorkflowForm({
    query,
    nodes,
    isCreateMode,
    isEditMode,
    workflow,
    projectId: projectId as string,
    activeWorkflowVersion,
    orderedWorkflows,
    edges,
    currentWorkflow,
    createWorkflowMutation,
    updateWorkflowMutation,
    setIsCreateMode,
    setIsEditMode,
    setIsDuplicated,
    setLastWorkflowUpdatePayload,
    hasSchemaChanged,
  })

  // Computed values
  const shouldDisableAddWorkflowButton =
    createWorkflowMutation.isLoading ||
    currentWorkflow["workflowId"] === "new" ||
    updateWorkflowMutation.isLoading ||
    isCreateMode ||
    isWorkflowLoading ||
    isWorkflowsLoading ||
    isFetching ||
    isRefetching

  const shouldHideSidebar = (!orderedWorkflows || orderedWorkflows.length === 0) && !searchValue

  // UseEffects
  /**
   * Updates workflow URL parameters and navigation state when workflow data changes.
   * Handles setting default workflow when no workflows exist and managing URL state.
   */
  useEffect(() => {
    const adjustedSearchParams = new URLSearchParams()

    if (!isWorkflowLoading && orderedWorkflows?.length === 0) {
      adjustedSearchParams.set("workflowName", "Untitled-1")
      adjustedSearchParams.set("workflowId", "new")
      setEnableClose("false")
    } else if (orderedWorkflows?.length > 0 && !query?.workflowId) {
      // reading queryParams and setting the active workflow based on it
      const workflowName = orderedWorkflows[0]?.name
      const workflowId = orderedWorkflows[0]?.uuid
      adjustedSearchParams.set("workflowName", workflowName as string)
      adjustedSearchParams.set("workflowId", workflowId as string)
      setEnableClose("false")
    } else {
      // In case you need to handle other scenarios, adjust or manipulate searchParams as needed
      // For example, you might want to append all the current query parameters
      Object.entries(query).forEach(([key, value]) => {
        searchParams.set(key, value)
      })
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      {
        replace: true,
      },
    )
    // Dependencies array: React guarantees that location.pathname is stable and won’t change on re-renders,
    // but you should include all variables used inside useEffect to the dependencies array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, navigate, location.pathname, orderedWorkflows, isWorkflowLoading])

  /**
   * this function takes the pre-defined nodes we want to add
   * and assign edges for them, then update nodes, edges states
   * TODO: one of the biggest refactors will happen alongside revamp is data-flow for updating nodes
   * and edges, currently UpdateGraph handles the following
   * 1. adding any nodes (either from add block or in-between)
   * 2. updating states (nodes, edges) on add and on delete, because there's a technical limitation
   * that needs further debugging when using edges from the store rather than from useEdgesState
   * so updateGraph should be refactored later
   * 3. refactor updateGraph params
   */
  const updateGraph = ({
    dataAfterDeletion,
    arrayOfNewNodes,
    isNodeBetween,
    targetNode,
    sourceNode,
    edgeLabel,
    areBranchesSwapped,
  }: UpdateGraphParams): void => {
    // first condition, if we are passing the newly data after deletion to this update function
    // to update our state with the passed data (nodes, edges), as well as updating the layouting function
    // TODO: make this in its own separate function
    if (dataAfterDeletion?.nodes?.length) {
      setEdges(dataAfterDeletion?.edges?.length ? dataAfterDeletion?.edges : getEdges())
      setNodes(dataAfterDeletion?.nodes)
      getLayoutedElements(
        dataAfterDeletion?.nodes,
        dataAfterDeletion?.edges?.length ? dataAfterDeletion?.edges : getEdges(),
      )
    }

    // then, we check if this update should happen because the filter branches swapped
    else if (areBranchesSwapped?.filterNodeId) {
      // first get the 2 children edges for the filter
      const filterChildren = getOutgoers(getNode(areBranchesSwapped.filterNodeId) as Node, getNodes(), getEdges())

      const filterChildrenEdges = getEdges()?.filter((edge) => edge.source === areBranchesSwapped.filterNodeId)

      if (filterChildrenEdges?.length === 2) {
        // swap the targets
        const modifiedTrueRoute = { ...filterChildrenEdges[0], target: filterChildrenEdges[1]?.target }
        const modifiedFalseRoute = { ...filterChildrenEdges[1], target: filterChildrenEdges[0]?.target }

        // remove old edges and add the newly ones
        setEdges([
          ...getEdges()?.filter((edge) => !filterChildrenEdges.some((edge2) => edge.id === edge2.id)),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          modifiedTrueRoute as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          modifiedFalseRoute as any,
        ])

        // updating the nodes
        setNodes(
          getNodes().map((node) => {
            if (
              node.id !== areBranchesSwapped.filterNodeId &&
              !filterChildren?.find((innerNode) => innerNode.id === node.id)
            ) {
              return node
            }

            const newNode = { ...node }

            // Adjust the `areBranchesSwapped` state
            if (newNode.id === areBranchesSwapped.filterNodeId) {
              newNode.data = {
                ...newNode.data,
                areBranchesSwapped: !newNode?.data?.areBranchesSwapped,
              }
            }

            // Update the `filterBranch` state if the node's filterId matches the swapped filterId
            if (filterChildren?.find((innerNode) => newNode.id === innerNode.id)) {
              const mappedBranches = {
                true: "false",
                false: "true",
              }

              newNode.data = {
                ...newNode.data,
                filterBranch: mappedBranches[newNode?.data?.filterBranch as keyof typeof mappedBranches] || "",
              }
            }

            return newNode
          }),
        )
      }
    }

    // then we check, if there're newly passed nodes or not
    else if (arrayOfNewNodes && arrayOfNewNodes?.length > 0) {
      const newEdges: Edge[] = []
      const sourceHandles = ["a", "b"]
      const firstNodeType = arrayOfNewNodes[0]?.data?.nodeType
      const isNewNodeFilterAndInLoop = Boolean(firstNodeType === "FilterNode" && arrayOfNewNodes[0]?.data?.isNodeInLoop)
      const loopEndOfFilterLoop = getNodes()?.find(
        (node) => node?.data?.parentLoopId === arrayOfNewNodes[0]?.data?.loopId,
      )

      // check if this new node(S) in between or at the end of the flow
      if (isNodeBetween) {
        const newInBetweenEdges: Edge[] = []

        for (let i = 0; i < arrayOfNewNodes?.length - 1; i++) {
          const newEdge = {
            id: `${uuidv4()}`,
            source: `${arrayOfNewNodes[i]?.id}`,
            target: `${arrayOfNewNodes[i + 1]?.id}`,
            sourceHandle: firstNodeType === "FilterNode" ? "b" : sourceHandles[i],
            targetHandle: arrayOfNewNodes[i + 1]?.data?.filterBranch === "false" ? "b" : "a",
            markerEnd,
            type: "custom",
            data: {
              text: firstNodeType === "FilterNode" ? "false" : getEdgeLabel(firstNodeType, i),
              updateGraph,
            },
            style: edgeStyles,
          }
          newInBetweenEdges.push(newEdge)
        }

        // OG node
        const previousNode = getNodes()?.filter((node) => node.id === sourceNode)

        // change the parentId of the previous connected node to the parentId of this new node
        setNodes([
          ...getNodes().map((node) => {
            const newNode = { ...node }

            if (node?.id === targetNode) {
              if (firstNodeType === "FilterNode") {
                newNode.data = {
                  ...newNode.data,
                  filterBranch: edgeLabel || "true",
                  filterId: previousNode[0]?.data?.filterId,
                  isNodeInFilter: true,
                  is_true_route: node?.data?.filterBranch === "true",
                  is_false_route: true,
                }
              }

              newNode.data = {
                ...newNode.data,
                parentId:
                  firstNodeType === "FilterNode"
                    ? arrayOfNewNodes[0]?.id
                    : arrayOfNewNodes[arrayOfNewNodes?.length - 1]?.id,
              }
            }

            return newNode
          }),
          ...arrayOfNewNodes,
        ])

        // creating new edge between the parentId and new node id (source: parentId, target: node id)
        const newEdge = {
          id: `${uuidv4()}`,
          source: `${arrayOfNewNodes[0]?.data?.parentId}`,
          target: `${arrayOfNewNodes[0]?.id}`,
          sourceHandle: edgeLabel === "true" ? "a" : "b",
          targetHandle: arrayOfNewNodes[0]?.data?.filterBranch === "false" ? "b" : "a",
          markerEnd,
          animated: arrayOfNewNodes[0]?.data?.parentId === "1",
          type: "custom",
          data: {
            updateGraph,
            text: edgeLabel ?? getEdgeLabel(previousNode[0]?.data?.nodeType, 0),
          },
          style: edgeStyles,
        }

        /**
         * creating new edge object to connect between new node id and the target node
         * which is the node that was previously connected to the parent of this new node
         * ex: new C node has been added, so: Source: C -> Target:targetNode
         */
        const modifiedEdge = {
          id: `${uuidv4()}`,
          source: `${arrayOfNewNodes[firstNodeType === "FilterNode" ? 0 : arrayOfNewNodes?.length - 1]?.id}`,
          target: `${targetNode}`,
          sourceHandle: "a",
          targetHandle:
            [...getEdges()].find(
              (edge) => edge.source === arrayOfNewNodes[0]?.data?.parentId && edge?.data?.text === edgeLabel,
            )?.targetHandle ?? `${uuidv4()}`,
          markerEnd,
          type: "custom",
          data: {
            text: getEdgeLabel(firstNodeType, 0),
            updateGraph,
          },
          style: edgeStyles,
        }

        // this edge is only valid, if we are adding a filter node inside a loop, in this case
        // we have 2 scenarios:
        // 1. both filter routes pointing to loop end node
        // 2. one route pointing to loop end node and the other pointing to another node, ex: program node
        // so we need this extra edge
        const secondModifiedEdge = {
          id: `${uuidv4()}`,
          source: `${arrayOfNewNodes[firstNodeType === "FilterNode" ? 0 : arrayOfNewNodes?.length - 1]?.id}`,
          target:
            isNewNodeFilterAndInLoop && getNode(targetNode as string)?.data?.nodeType !== "LoopEndNode"
              ? loopEndOfFilterLoop?.id
              : `${targetNode}`,
          sourceHandle: "b",
          targetHandle: `${uuidv4()}`,
          markerEnd,
          type: "custom",
          data: {
            text: "false",
            updateGraph,
          },
          style: edgeStyles,
        }

        /**
         * Find the index of the edge that we will to remove.
         * the edge that was connecting the parent node and the old node before adding the new node to be in between
         * ex: edge1 = Source:A -> Target:B, now new C node added so now we will remove this edge1
         * we have an exception which is FilterNode because the parent has 2 children (true and false)
         * so we need to check on the edge Label with the parentId
         */
        const indexToRemove = [...getEdges()].findIndex((edge) => {
          return edge.source === arrayOfNewNodes[0]?.data?.parentId && edge?.data?.text === edgeLabel
        })

        // creating new edges array will be used with filter node
        const adjustedEdges = [...getEdges()]

        // Remove the element from the edges array.
        adjustedEdges.splice(indexToRemove, 1)

        const validEdges = getEdges().filter((edge: Edge) => edge.source !== arrayOfNewNodes[0]?.data?.parentId)

        const updatedEdges: Edge[] = []
        // updating the edges with two different arrays based on the filter condition
        if (previousNode[0]?.type === "FilterNode") {
          /**
           * this check is IMPORTANT, because the edge with the sourceHandle = "a"
           * which is in this case the one with edgeLabel = "true" MUST come before
           * the edge with the sourceHandle = "b" (edgeLabel = "false") in the order of the array, that's why
           * the newEdge comes in first when the edgeLabel === "true" to ensure that SH -> a is before SH -> b
           */
          if (edgeLabel === "true") {
            updatedEdges.push(newEdge, ...adjustedEdges, modifiedEdge)
          } else {
            updatedEdges.push(...adjustedEdges, newEdge, modifiedEdge)
          }
        } else {
          updatedEdges.push(...validEdges, newEdge, modifiedEdge)
        }

        // Conditionally add secondModifiedEdge and newInBetweenEdges
        if (isNewNodeFilterAndInLoop) {
          updatedEdges.push(secondModifiedEdge as Edge, ...newInBetweenEdges)
        } else {
          updatedEdges.push(...newInBetweenEdges)
        }

        // Updating the updatedEdges with modifying the old edge
        setEdges(updatedEdges)

        getLayoutedElements([...getNodes()], updatedEdges)
      } else {
        for (let i = 0; i < arrayOfNewNodes?.length - 1; i++) {
          const newEdge = {
            id: `${uuidv4()}`,
            source:
              arrayOfNewNodes[0]?.type === "FilterNode" ? `${arrayOfNewNodes[0]?.id}` : `${arrayOfNewNodes[i]?.id}`,
            target: `${arrayOfNewNodes[i + 1]?.id}`,
            sourceHandle: sourceHandles[i],
            markerEnd,
            type: "custom",
            data: {
              text: getEdgeLabel(firstNodeType, i),
              updateGraph,
            },
            style: edgeStyles,
          }
          newEdges.push(newEdge)
        }
        setNodes([...getNodes(), ...arrayOfNewNodes])
        setEdges([...getEdges(), ...newEdges])
        getLayoutedElements([...getNodes(), ...arrayOfNewNodes], [...edges, ...newEdges])
      }
    }
  }

  /**
   * Initializes default nodes and edges for new workflows.
   * Creates the start node and add block node when creating a new workflow.
   */
  useEffect(() => {
    if ((currentWorkflow["workflowId"] === "new" || query.workflowId === "new") && isCreateMode) {
      if (!isDuplicated) {
        initNodesAndEdges()
      }
      setSearchParams({
        workflowName: searchParams.get("workflowName") as string,
        workflowId: searchParams.get("workflowId") as string,
      })
      setEnableClose("true")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasWorkflowOpen, query.workflowId, isCreateMode, isDuplicated])

  /**
   * Updates canvas nodes and edges when workflow data is fetched from the backend.
   * Handles both empty workflows and workflows with existing nodes.
   */
  useEffect(() => {
    if (workflow?.data?.name && !isFetching && !isWorkflowLoading && !isWorkflowsLoading) {
      if (
        activeWorkflowVersion?.nodes &&
        activeWorkflowVersion?.nodes?.length === 0 &&
        activeWorkflowVersion?.start_node === null &&
        !updateWorkflowMutation.isLoading &&
        !isCreateMode &&
        !isEditMode
      ) {
        nodelessWorkFlow(true)
      } else if (
        activeWorkflowVersion?.nodes &&
        activeWorkflowVersion?.nodes?.length > 0 &&
        activeWorkflowVersion?.start_node !== null &&
        !isCreateMode &&
        !isEditMode
      ) {
        // parsing the fetched workflow
        const flowData = convertRetrievedNodesToFlow(
          activeWorkflowVersion?.nodes,
          activeWorkflowVersion?.start_node ?? "0",
          "retrieve",
          updateGraph,
          activeWorkflowVersion?.schema,
          formik,
        )
        // to ensure canvas is re-triggered
        adjustCanvasNodes([...flowData["nodes"]], [...flowData["edges"]])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeWorkflowVersion,
    createWorkflowMutation.isSuccess,
    updateWorkflowMutation.isSuccess,
    updateWorkflowMutation.isLoading,
    isWorkflowLoading,
    isWorkflowsLoading,
    isFetching,
    isCreateMode,
    isEditMode,
  ])

  // Execute workflow layout function
  getLayoutedElements(getNodes(), edges)

  /**
   * Sets create mode to false when a workflow is successfully fetched.
   * Ensures proper mode switching between create and view modes.
   */
  useEffect(() => {
    if (!isWorkflowLoading && !isFetching && workflow?.data?.name && currentWorkflow["workflowId"] !== "new") {
      setIsCreateMode(false)
    }
  }, [workflow, isWorkflowLoading, currentWorkflow, isFetching])

  /**
   * Sets the active workflow based on URL query parameters.
   * Automatically selects the first workflow if no specific workflow is specified.
   */
  useEffect(() => {
    if (orderedWorkflows?.length > 0 && !query?.workflowId) {
      setCurrentWorkflow({
        workflowName: orderedWorkflows[0]?.name as string,
        workflowId: orderedWorkflows[0]?.uuid as string,
      })
    }
  }, [query?.workflowId, orderedWorkflows])

  /**
   * Populates form fields with workflow schema data when a workflow is loaded.
   * Extracts features, constants, and computed features from the active workflow version.
   */
  useEffect(() => {
    if (activeWorkflowVersion && searchParams.get("workflowId") !== "new") {
      const newArr: Array<WorkflowInputFeature & { id: string }> = []
      const computedFeaturesArr: Array<{ uuid: string; value: string }> = []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      activeWorkflowVersion?.nodes?.forEach((node: any) => {
        if (node?.computed_feature_name) {
          computedFeaturesArr.push({ uuid: node?.uuid, value: node?.computed_feature_name })
        } else if (node.calculator?.equations) {
          computedFeaturesArr.push(
            ...node.calculator.equations.map((equation: CalculatorEquation) => ({
              uuid: node?.uuid,
              value: equation?.computed_feature_name,
            })),
          )
        }
      })
      activeWorkflowVersion?.schema?.features?.forEach((feat) => {
        newArr.push({
          id: uuidv4(),
          name: feat?.name,
          type: feat?.type,
          is_used: feat?.is_used,
          is_required: feat?.is_required,
          source: feat?.source as WorkflowInputFeature.source,
        })
      })

      const newConstantsArr: Array<WorkflowConstantFeature & { id: string }> = []

      activeWorkflowVersion?.constants?.forEach((constant) => {
        newConstantsArr.push({
          id: uuidv4(),
          ...constant,
        })
      })

      formik.resetForm({
        values: {
          ...formik.values,
          features: newArr,
          constants: newConstantsArr,
          computedFeatures: computedFeaturesArr,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkflowVersion?.schema, isWorkflowLoading, searchParams, isFetching, isCreateMode])

  /**
   * Updates node data with current mode information (create/edit/view).
   * Ensures nodes have access to formik instance and mode flags.
   */
  useEffect(() => {
    if (isEditMode || isCreateMode) {
      setNodes(
        getNodes().map((node) => {
          const newNode = { ...node }
          newNode.data = {
            ...newNode.data,
            createMode: isCreateMode,
            editMode: isEditMode,
            readMode: !(isCreateMode || isEditMode),
            formik,
          }
          return newNode
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.features, isEditMode, getNodes(), formik.values.computedFeatures, isCreateMode])

  /**
   * Initializes session storage state on component mount.
   * Ensures proper enableClose state management for workflow creation.
   */
  useEffect(() => {
    if (searchParams.get("workflowId") !== "new") {
      setSearchParams({
        workflowName: searchParams.get("workflowName") as string,
        workflowId: searchParams.get("workflowId") as string,
      })
      setEnableClose("false")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Fragment>
      <BaseSimpleDialog
        open={confirmDeprecateWorkflowsDialogOpen}
        name=""
        onClose={() => {
          setConfirmDeprecateWorkflowsDialogOpen(false)

          if (formik.values.isSchemaSubmit) {
            formik.resetForm()
            formik.setFieldValue("isSchemaSubmit", false)
          }
        }}
        onAccept={() => {
          updateWorkflowMutation.mutateAsync({
            ...(lastWorkflowUpdatePayload as UpdateWorkflowRequest),
            deprecate_workflows: true,
          })
        }}
        mode={isCreateMode || isEditMode ? "deprecate-workflows" : "view-mode-deprecate-workflows"}
        list={collidingFeaturesList}
        isLoading={updateWorkflowMutation.isLoading}
      />

      <BaseSimpleDialog
        onClose={() => setConfirmCloseDialogOpen({ isOpen: false })}
        open={confirmCloseDialogOpen?.isOpen}
        mode="close-while-edit"
        isLoading={false}
        name=""
        onAccept={handleConfirmAction}
      />

      <Box className={styles.workflowsContainer}>
        <WorkflowCanvasContext.Provider
          value={{
            mode: isCreateMode && currentWorkflow["workflowId"] === "new" ? "create" : isEditMode ? "edit" : "view",
            isWorkflowLoading,
            isWorkflowFetching: isFetching,
            isWorkflowsLoading,
            workflow: workflow?.data,
            formik,
            onEdgesChange,
          }}
        >
          {/* Sidebar Section */}
          {isSidebarOpen && !shouldHideSidebar && (
            <WorkflowSidebar
              searchValue={searchValue}
              actions={
                <Grid container>
                  <Grid item xs={12} marginBottom={1}>
                    <Button
                      key={workflow?.data?.name}
                      size="small"
                      variant="primary"
                      fullWidth
                      onClick={onAddNewWorkflow}
                      disabled={shouldDisableAddWorkflowButton}
                      startIcon={
                        <AddCircleOutlineOutlinedIcon
                          sx={{
                            width: "15px",
                            height: "15px",
                          }}
                        />
                      }
                    >
                      {t("addWorkflowButton")}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <DebouncedInput
                      fullWidth
                      variant="filled"
                      callback={(value) => {
                        setSearchValue(() => value as string)
                      }}
                    />
                  </Grid>
                </Grid>
              }
              setActiveWorkflow={setCurrentWorkflow}
              orderedWorkflows={orderedWorkflows}
              workflowsLength={workflowsData?.pages[0]?.data?.count ?? 0}
              setIsEditMode={setIsEditMode}
              hasMore={hasNextPage ?? false}
              fetchNext={fetchNextPage}
              shouldFetchNext={!isWorkflowsLoading && !isFetchingNextPage}
              setConfirmCloseDialogOpen={setConfirmCloseDialogOpen}
            />
          )}

          {/* Main Content Section */}
          {!isWorkflowsLoading &&
          !isWorkflowLoading &&
          !orderedWorkflows?.length &&
          !canvasWorkflowOpen &&
          !searchValue ? (
            // Empty State
            <Grid item xs={12} container minHeight={"100%"} className="dotted-background">
              <KonanEmptyState
                title={t("emptyState.title")}
                subTitle={t("emptyState.subtitle")}
                buttonText={t("emptyState.buttonText")}
                setAction={handleInitNewWorkflow}
              />
            </Grid>
          ) : (
            // Workflow Canvas Section
            <Grid item container xs={12} className={`${styles.workflowViewContainer} layoutflow`}>
              {isWorkflowLoading ||
              createWorkflowMutation.isLoading ||
              updateWorkflowMutation.isLoading ||
              isFetching ||
              nodes?.length === 0 ||
              isRefetching ? (
                // Loading State
                <Grid item xs={12} container minHeight={"80%"} className="dotted-background">
                  <InfoContainer icon={<CircularProgress />} title={t("loadingState")} />
                </Grid>
              ) : (
                // Active Workflow Canvas
                <WorkflowCanvas
                  nodes={getNodes()}
                  edges={edges}
                  setIsSidebarOpen={setIsSidebarOpen}
                  workflowControls={
                    <WorkflowControls
                      isWorkflowRefetching={isRefetching}
                      workflowsCount={orderedWorkflows?.length}
                      isCreateWorkflowMutationLoading={createWorkflowMutation.isLoading}
                      isUpdateWorkflowMutationLoading={updateWorkflowMutation.isLoading}
                      handleEditClicked={handleEditClicked}
                      handleDuplicateWorkflow={handleDuplicateWorkflow}
                      setConfirmCloseDialogOpen={setConfirmCloseDialogOpen}
                    />
                  }
                />
              )}
            </Grid>
          )}
        </WorkflowCanvasContext.Provider>
      </Box>
    </Fragment>
  )
}

export const FlowWithProvider = memo(
  (): React.ReactElement => (
    <div className="zoompanflow">
      <ReactFlowProvider>
        <Workflows />
      </ReactFlowProvider>
    </div>
  ),
)

export { Workflows }
